import { useState, type ReactNode } from "react";
import { postJson } from "../api";
import { useAiData, useTitle } from "../ai/parts";
import { Counter, RowsSkeleton, Window, useFilters, useIsMobile } from "../ai/widgets";
import ui from "../ai/ai.module.css";
import { OrgNotFound } from "../pages/StatePages";
import { useOpenOrg } from "../shell/openOrg";
import { formatText, useTexts } from "../texts";
import { ChoiceMenu } from "../ui/ChoiceMenu";
import { ChatIcon } from "../ui/icons/Icon";
import { Notice, PageHead, TextField, useToast } from "../ui/kit";
import { ListFooter, useServerList, type ServerList } from "../ui/list";
import { type OrgHead } from "./employees";
import { PastEnd } from "./ConversationsPages";
import { BlockFailed, Failed, replyMessage } from "./EmployeesPages";
import {
  channelLine,
  channelOption,
  messagingTabs,
  modeText,
  originTitle,
  type Channel,
  type Inbox,
  type InboxOption,
  type MessagingTab,
  type Origin,
  type Rule,
} from "./messaging";
import { plural } from "./organizations";
import { CellSwitch, SimpleTable } from "./SimpleTable";
import styles from "./orgs.module.css";
import own from "./messaging.module.css";

// R6 · Empresa › Mensajería (§5.7): Bandejas (solo lectura), Canales (bandeja predeterminada) y Reglas de entrada (reglas y
// orígenes detectados, paginados en Messaging, B3). Las conversaciones internas y el simulador están en sus archivos.

/** Encabezado de la sección (C03) con sus cinco pestañas; las acciones de la pestaña van a la derecha. */
export function MessagingHead({
  orgId,
  current,
  sub,
  children,
}: {
  orgId: number;
  current: MessagingTab;
  sub?: string;
  children?: ReactNode;
}) {
  const { L } = useTexts();
  return (
    <>
      <PageHead title={L("ADM_ORG_MESSAGING")} sub={sub}>
        {children}
      </PageHead>
      <nav className={`tm-tabs ${ui.tabs}`} aria-label={L("ADM_ORG_MESSAGING")} data-part="pestanas">
        {messagingTabs.map((tab) => (
          <a
            key={tab.key}
            href={`${tab.path}?id=${orgId}`}
            aria-current={tab.key === current ? "page" : undefined}
          >
            {L(tab.labelKey)}
          </a>
        ))}
      </nav>
    </>
  );
}

/** Logo del canal (E27: el de WhatsApp para WhatsApp; otro canal, el ícono genérico de mensaje). */
export function ChannelMark({ type, large }: { type: string; large?: boolean }) {
  const { L } = useTexts();
  const whatsapp = type === "WHATSAPP";
  return (
    <span
      className={`${own.channelMark}${whatsapp ? ` ${own.whatsapp}` : ""}${large ? ` ${own.channelMarkLarge}` : ""}`}
      role="img"
      aria-label={formatText(L("ADM_MSG_CHANNEL_LABEL"), whatsapp ? L("CHTYPE_WHATSAPP") : L("ADM_MSG_CHANNEL_OTHER"))}
    >
      <ChatIcon name={whatsapp ? "whatsapp" : "message-square-text"} />
    </span>
  );
}

type InboxList = { organization: OrgHead; items: Inbox[]; total: number };

/**
 * `emp-bandejas` · solo lectura (se crean y editan en el Company Portal, la descripción lo dice), sin ⋮. «N miembros»
 * lleva a Personas › Usuarios con el filtro de esa bandeja (el mismo número que allá, E84).
 */
export function InboxesPage({ orgId }: { orgId: number }) {
  const { L, locale } = useTexts();
  const mobile = useIsMobile();
  const data = useAiData<InboxList>(`/api/admin/organizations/${orgId}/inboxes`);
  useOpenOrg(data.data?.organization ?? null);
  useTitle(`${L("ADM_ORG_MESSAGING")} · ${L("ADM_MSG_TAB_INBOXES")}`);
  if (data.state === "missing") return <OrgNotFound />;
  const head = <MessagingHead orgId={orgId} current="inboxes" sub={L("ADM_MSG_INBOXES_LINE")} />;
  const list = data.data;
  if (!list) {
    if (data.state === "failed") return <Failed head={head} status={data.error?.status} retry={data.retry} />;
    return (
      <>
        {head}
        <Counter>
          <span className={ui.bone} style={{ display: "inline-block", width: 80, height: 12 }} />
        </Counter>
        <RowsSkeleton rows={3} table={!mobile} />
      </>
    );
  }
  const members = (inbox: Inbox) => {
    const text = plural(L, locale, inbox.members, "ADM_MSG_N_MEMBERS_ONE", "ADM_MSG_N_MEMBERS");
    return inbox.members > 0 ? (
      <a className={own.link} href={`/Organizations/Usuarios?id=${orgId}&inbox=${inbox.id}`}>
        {text}
      </a>
    ) : (
      text
    );
  };
  const name = (inbox: Inbox) => (
    <span className={own.twoLines}>
      <span className={own.titleRow}>
        <strong title={inbox.name}>{inbox.name}</strong>
        {inbox.fallback ? <span className="tm-tag">{L("ADM_MSG_GENERAL")}</span> : null}
      </span>
      {inbox.description ? <span title={inbox.description}>{inbox.description}</span> : null}
    </span>
  );
  return (
    <>
      {head}
      {list.items.length === 0 ? (
        <div className="tm-empty" role="status" data-part="vacio">
          <strong>{L("ADM_MSG_INBOXES_EMPTY")}</strong>
          <span>{L("ADM_MSG_INBOXES_EMPTY_LINE")}</span>
          <a className="tm-btn secondary" href={`/Organizations/Detail?id=${orgId}`}>
            {L("ADM_MSG_BACK_SUMMARY")}
          </a>
        </div>
      ) : (
        <>
          <Counter>{plural(L, locale, list.total, "ADM_MSG_N_INBOXES_ONE", "ADM_MSG_N_INBOXES")}</Counter>
          <div className={own.fit}>
            <SimpleTable
              label={L("ADM_MSG_TAB_INBOXES")}
              rows={list.items}
              rowKey={(inbox) => inbox.id}
              columns={[
                { key: "name", title: L("ADM_MSG_COL_INBOX"), render: name },
                {
                  key: "status",
                  title: L("ADM_EMP_COL_STATUS"),
                  render: (inbox) => L(inbox.active ? "ADM_KB_ACTIVE" : "ADM_KB_INACTIVE"),
                },
                { key: "mode", title: L("ADM_MSG_COL_MODE"), render: (inbox) => modeText(L, inbox.mode) },
                { key: "members", title: L("ADM_MSG_COL_MEMBERS"), render: members },
                {
                  key: "open",
                  title: L("ADM_MSG_COL_OPEN"),
                  numeric: true,
                  render: (inbox) => inbox.open.toLocaleString(locale),
                },
              ]}
              mobile={(inbox) => ({
                title: inbox.name,
                tag: inbox.fallback ? <span className="tm-tag">{L("ADM_MSG_GENERAL")}</span> : undefined,
                line: [
                  L(inbox.active ? "ADM_KB_ACTIVE" : "ADM_KB_INACTIVE"),
                  modeText(L, inbox.mode),
                  plural(L, locale, inbox.members, "ADM_MSG_N_MEMBERS_ONE", "ADM_MSG_N_MEMBERS"),
                  plural(L, locale, inbox.open, "ADM_MSG_N_OPEN_ONE", "ADM_MSG_N_OPEN"),
                ].join(" · "),
              })}
            />
          </div>
        </>
      )}
    </>
  );
}

type ChannelList = { organization: OrgHead; items: Channel[]; total: number; inboxes: InboxOption[] | null };

/** Selector en celda (C08): muestra la bandeja y, al tocarlo, abre la ventana que confirma el cambio (D8). */
function InboxPick({ channel, onPick }: { channel: Channel; onPick: (channel: Channel) => void }) {
  const { L } = useTexts();
  return (
    <button
      type="button"
      className={own.cellPick}
      aria-haspopup="dialog"
      aria-label={formatText(L("ADM_MSG_DEFAULT_FOR"), channel.name)}
      onClick={() => onPick(channel)}
    >
      <span data-empty={channel.defaultInbox ? undefined : "true"} title={channel.defaultInbox ?? undefined}>
        {channel.defaultInbox ?? L("ADM_MSG_PICK_INBOX")}
      </span>
      <ChatIcon name="chevron-down" />
    </button>
  );
}

/**
 * `emp-canales` · cada canal con su logo, número, estado, bandeja predeterminada (selector en celda → ventana
 * `bandeja-canal`, D8) y sus reglas de entrada (enlace a esa pestaña con el canal elegido). En el celular la fila abre
 * la misma ventana.
 */
export function ChannelsPage({ orgId }: { orgId: number }) {
  const { L, locale } = useTexts();
  const mobile = useIsMobile();
  const data = useAiData<ChannelList>(`/api/admin/organizations/${orgId}/channels`);
  const [picking, setPicking] = useState<Channel | null>(null);
  useOpenOrg(data.data?.organization ?? null);
  useTitle(`${L("ADM_ORG_MESSAGING")} · ${L("ADM_MSG_TAB_CHANNELS")}`);
  if (data.state === "missing") return <OrgNotFound />;
  const head = <MessagingHead orgId={orgId} current="channels" />;
  const list = data.data;
  if (!list) {
    if (data.state === "failed") return <Failed head={head} status={data.error?.status} retry={data.retry} />;
    return (
      <>
        {head}
        <Counter>
          <span className={ui.bone} style={{ display: "inline-block", width: 80, height: 12 }} />
        </Counter>
        <RowsSkeleton rows={4} table={!mobile} />
      </>
    );
  }
  const rulesHref = (channel: Channel) => `/Organizations/Reglas?id=${orgId}&channelId=${channel.id}`;
  const rulesCell = (channel: Channel) =>
    channel.rules == null ? (
      <span title={L("ADM_ORGS_NOT_AVAILABLE")}>—</span>
    ) : (
      <a className={own.link} href={rulesHref(channel)}>
        {channel.rules === 0
          ? L("ADM_MSG_NO_RULES")
          : plural(L, locale, channel.rules, "ADM_MSG_N_RULES_ONE", "ADM_MSG_N_RULES")}
      </a>
    );
  const tags = (channel: Channel) => (
    <>
      {!channel.defaultInboxId ? <span className="tm-tag ochre">{L("ADM_MSG_NO_INBOX")}</span> : null}
      {channel.simulation ? <span className="tm-tag">{L("CH_SIM")}</span> : null}
    </>
  );
  const status = (channel: Channel) => L(channel.active ? "MSHIP_ACTIVE" : "MSHIP_INACTIVE");
  return (
    <>
      {head}
      {list.items.length === 0 ? (
        <div className="tm-empty" role="status" data-part="vacio">
          <strong>{L("ADM_MSG_CHANNELS_EMPTY")}</strong>
          <span>{L("ADM_MSG_CHANNELS_EMPTY_LINE")}</span>
          <a className="tm-btn secondary" href={`/Organizations/Bandejas?id=${orgId}`}>
            {L("ADM_MSG_SEE_INBOXES")}
          </a>
        </div>
      ) : (
        <>
          <Counter>{plural(L, locale, list.total, "ADM_MSG_N_CHANNELS_ONE", "ADM_MSG_N_CHANNELS")}</Counter>
          {list.inboxes == null ? <BlockFailed retry={data.retry} /> : null}
          {mobile ? (
            <ul className={`tm-list ${own.list}`} aria-label={L("ADM_MSG_TAB_CHANNELS")} data-part="lista">
              {list.items.map((channel) => (
                <li key={channel.id} className="tm-row">
                  <button
                    type="button"
                    className={`tm-row-main ${own.rowButton}`}
                    aria-haspopup="dialog"
                    onClick={() => setPicking(channel)}
                  >
                    <ChannelMark type={channel.type} large />
                    <span className="tm-row-text">
                      <span className="tm-line1">
                        <strong className={own.rowTitle}>{channel.name}</strong>
                        {tags(channel)}
                      </span>
                      <span className={`tm-line2 ${own.ellipsis}`}>
                        {[channelLine(L, channel), status(channel), channel.defaultInbox ?? L("ADM_MSG_PICK_INBOX")].join(
                          " · ",
                        )}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className={own.fit}>
              <SimpleTable
                label={L("ADM_MSG_TAB_CHANNELS")}
                rows={list.items}
                rowKey={(channel) => channel.id}
                columns={[
                  {
                    key: "channel",
                    title: L("ADM_MSG_COL_CHANNEL"),
                    render: (channel) => (
                      <span className={own.withMark}>
                        <ChannelMark type={channel.type} />
                        <span className={own.twoLines}>
                          <span className={own.titleRow}>
                            <strong title={channel.name}>{channel.name}</strong>
                            {tags(channel)}
                          </span>
                          <span>{channelLine(L, channel)}</span>
                        </span>
                      </span>
                    ),
                  },
                  { key: "status", title: L("ADM_EMP_COL_STATUS"), render: status },
                  {
                    key: "inbox",
                    title: L("ADM_MSG_COL_DEFAULT"),
                    render: (channel) => <InboxPick channel={channel} onPick={setPicking} />,
                  },
                  { key: "rules", title: L("ADM_MSG_TAB_RULES"), render: rulesCell },
                ]}
                mobile={(channel) => ({ title: channel.name })}
              />
            </div>
          )}
        </>
      )}
      <DefaultInboxWindow
        orgId={orgId}
        channel={picking}
        inboxes={list.inboxes}
        onClose={() => setPicking(null)}
        onSaved={() => void data.reload()}
      />
    </>
  );
}

/**
 * Ventana `bandeja-canal` · «Bandeja de {canal}»: elegir la bandeja y el aviso dice adónde va el trabajo (las nuevas a
 * esa bandeja, las abiertas se quedan). Cambiar confirma en la misma ventana (D8); el toast dice el resultado.
 */
function DefaultInboxWindow({
  orgId,
  channel,
  inboxes,
  onClose,
  onSaved,
}: {
  orgId: number;
  channel: Channel | null;
  inboxes: InboxOption[] | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { L } = useTexts();
  const toast = useToast();
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [shown, setShown] = useState<number | null>(null);
  if ((channel?.id ?? null) !== shown) {
    setShown(channel?.id ?? null);
    if (channel) {
      setValue(channel.defaultInboxId ? String(channel.defaultInboxId) : "");
      setError(null);
    }
  }
  const options = (inboxes ?? []).map((inbox) => ({ value: String(inbox.id), label: inbox.name }));
  const chosen = options.find((option) => option.value === value)?.label ?? null;
  const unchanged = !!channel && value === String(channel.defaultInboxId ?? "");
  const save = async () => {
    if (!channel) return;
    if (!value) {
      setError(L("ORGRULES_ERR_TARGET"));
      return;
    }
    if (unchanged) {
      onClose();
      return;
    }
    setBusy(true);
    const reply = await postJson<{ saved?: boolean; messageKey?: string }>(
      `/api/admin/organizations/${orgId}/channels/${channel.id}/default-inbox`,
      { inboxId: Number(value) },
    );
    setBusy(false);
    if (reply.status === 200 && reply.body?.saved) {
      toast.show(formatText(L("ADM_MSG_DEFAULT_SAVED"), channel.name, chosen ?? ""));
      onSaved();
      onClose();
      return;
    }
    setError(replyMessage(L, reply.body));
  };
  return (
    <Window
      open={!!channel}
      title={formatText(L("ADM_MSG_DEFAULT_TITLE"), channel?.name ?? "")}
      onClose={() => !busy && onClose()}
      footer={
        <>
          <button type="button" className="tm-btn secondary" onClick={onClose} disabled={busy}>
            {L("ACTION_CANCEL")}
          </button>
          <button
            type="button"
            className="tm-btn primary"
            disabled={busy || inboxes == null || options.length === 0}
            aria-busy={busy || undefined}
            onClick={() => void save()}
          >
            {L("ADM_AI_CHANGE")}
          </button>
        </>
      }
    >
      <div className={ui.windowBlock}>
        {inboxes == null ? (
          <Notice tone="error">{L("ADM_UNAVAILABLE_LINE")}</Notice>
        ) : options.length === 0 ? (
          <Notice tone="info">{L("ADM_MSG_NO_ACTIVE_INBOXES")}</Notice>
        ) : (
          <>
            <Notice tone="info">
              {chosen && !unchanged
                ? formatText(L("ADM_MSG_DEFAULT_IMPACT"), chosen)
                : L(channel?.defaultInboxId ? "ADM_MSG_DEFAULT_CURRENT" : "ADM_MSG_DEFAULT_NONE")}
            </Notice>
            <div className={`tm-field${error ? " invalid" : ""}`}>
              <label htmlFor="default-inbox">{L("ADM_MSG_COL_DEFAULT")}</label>
              <InboxChoice
                id="default-inbox"
                label={L("ADM_MSG_COL_DEFAULT")}
                value={value}
                options={options}
                invalid={!!error}
                onChange={(next) => (setValue(next), setError(null))}
              />
              {error ? <span className="tm-field-error">{error}</span> : null}
            </div>
          </>
        )}
      </div>
    </Window>
  );
}

/** Selector de bandeja (C08) con su búsqueda cuando son muchas. */
function InboxChoice({
  id,
  label,
  value,
  options,
  invalid,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  options: { value: string; label: string }[];
  invalid?: boolean;
  onChange: (value: string) => void;
}) {
  const { L } = useTexts();
  return (
    <ChoiceMenu
      id={id}
      label={label}
      value={value}
      placeholder={L("ADM_MSG_PICK_INBOX")}
      options={options}
      onChange={onChange}
      invalid={invalid}
      searchLabel={L("CFG1_SEARCH")}
      clearSearchLabel={L("CFG1_R1_CLEAR_SEARCH")}
      searchIcon={<ChatIcon name="search" />}
      clearIcon={<ChatIcon name="x" />}
      chevron={<ChatIcon name="chevron-down" />}
      check={<ChatIcon name="check" />}
    />
  );
}

/** Error de una lista con «Reintentar» (C14), nunca un vacío ni un 0 (H-3). */
function ListFailed({ list }: { list: Pick<ServerList<unknown>, "reload"> }) {
  return <BlockFailed retry={list.reload} />;
}

/**
 * `emp-reglas` · el canal se elige arriba y queda en la URL (`channelId`). Reglas del canal con su interruptor (toast con
 * «Deshacer», C13) y orígenes detectados con «Crear regla» por fila; cada lista se pagina en Messaging (B3): las reglas
 * con `rulesPage` y los orígenes con `page` (como la captura). «Nueva regla» abre la ventana `regla`.
 */
export function RulesPage({ orgId }: { orgId: number }) {
  const { L, locale } = useTexts();
  const toast = useToast();
  const mobile = useIsMobile();
  const filters = useFilters();
  const data = useAiData<ChannelList>(`/api/admin/organizations/${orgId}/channels`);
  const [creating, setCreating] = useState<{ origin: string } | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  useOpenOrg(data.data?.organization ?? null);
  useTitle(`${L("ADM_ORG_MESSAGING")} · ${L("ADM_MSG_TAB_RULES")}`);
  const channels = data.data?.items ?? [];
  const wanted = Number(filters.get("channelId"));
  // El canal de la dirección; sin él (o si no es de la empresa), el primero que no es de simulación.
  const channel =
    channels.find((item) => item.id === wanted) ?? channels.find((item) => !item.simulation) ?? channels[0] ?? null;
  const base = channel ? `/api/admin/organizations/${orgId}/channels/${channel.id}` : null;
  const rules = useServerList<Rule>(base ? `${base}/rules` : null, "rulesPage");
  const origins = useServerList<Origin>(base ? `${base}/origins` : null);
  if (data.state === "missing") return <OrgNotFound />;
  const add = (
    <>
      <button
        type="button"
        className={`tm-btn primary ${ui.desktopInline}`}
        disabled={!channel}
        onClick={() => setCreating({ origin: "" })}
      >
        {L("ADM_MSG_NEW_RULE")}
      </button>
      {channel ? (
        <button
          type="button"
          className={ui.fab}
          aria-label={L("ADM_MSG_NEW_RULE")}
          title={L("ADM_MSG_NEW_RULE")}
          onClick={() => setCreating({ origin: "" })}
        >
          <ChatIcon name="plus" />
        </button>
      ) : null}
    </>
  );
  const head = (
    <MessagingHead orgId={orgId} current="rules" sub={L("ADM_MSG_RULES_LINE")}>
      {add}
    </MessagingHead>
  );
  const list = data.data;
  if (!list) {
    if (data.state === "failed") return <Failed head={head} status={data.error?.status} retry={data.retry} />;
    return (
      <>
        {head}
        <div className={own.channelField}>
          <span className={ui.bone} style={{ display: "block", width: 60, height: 14, marginBottom: 8 }} />
          <span className={ui.bone} style={{ display: "block", width: "100%", height: 42 }} />
        </div>
        <RowsSkeleton rows={3} table={!mobile} />
      </>
    );
  }
  if (!channel)
    return (
      <>
        {head}
        <div className="tm-empty" role="status" data-part="vacio">
          <strong>{L("ADM_MSG_RULES_NO_CHANNELS")}</strong>
          <span>{L("ADM_MSG_RULES_NO_CHANNELS_LINE")}</span>
          <a className="tm-btn secondary" href={`/Organizations/Canales?id=${orgId}`}>
            {L("ADM_MSG_SEE_CHANNELS")}
          </a>
        </div>
      </>
    );
  const setStatus = async (rule: Rule, active: boolean, undo: boolean) => {
    setBusy(rule.id);
    const reply = await postJson<{ saved?: boolean; messageKey?: string }>(
      `/api/admin/organizations/${orgId}/rules/${rule.id}/status`,
      { status: active ? "ACTIVE" : "INACTIVE" },
    );
    setBusy(null);
    if (reply.status !== 200 || !reply.body?.saved) {
      toast.show(replyMessage(L, reply.body));
      return;
    }
    rules.reload();
    origins.reload();
    toast.show(
      L(active ? "ADM_MSG_RULE_ACTIVATED" : "ADM_MSG_RULE_DEACTIVATED"),
      undo ? { label: L("ADM_UNDO"), run: () => void setStatus(rule, !active, false) } : undefined,
    );
  };
  const ruleSwitch = (rule: Rule) => (
    <CellSwitch
      className={styles.switchCell}
      trackClass={styles.track}
      checked={rule.active}
      label={originTitle(rule)}
      text={L(rule.active ? "ADM_KB_ACTIVE" : "ADM_KB_INACTIVE")}
      disabled={busy === rule.id}
      onChange={(value) => void setStatus(rule, value, true)}
    />
  );
  const origin = (item: { headline: string | null; source: string }) => (
    <span className={own.twoLines}>
      <strong title={originTitle(item)}>{originTitle(item)}</strong>
      {item.headline ? <span title={item.source}>{item.source}</span> : null}
    </span>
  );
  const createFor = (item: Origin) =>
    item.ruleId ? null : (
      <button
        type="button"
        className={own.light}
        aria-label={formatText(L("ADM_MSG_CREATE_RULE_FOR"), originTitle(item))}
        onClick={() => setCreating({ origin: item.source })}
      >
        {L("ADM_MSG_CREATE_RULE")}
      </button>
    );
  return (
    <>
      {head}
      <div className={own.channelField}>
        <label className={styles.fieldTitle} htmlFor="rules-channel">
          {L("ORGRULES_CHANNEL")}
        </label>
        <ChoiceMenu
          id="rules-channel"
          label={L("ORGRULES_CHANNEL")}
          value={String(channel.id)}
          options={channels.map((item) => ({ value: String(item.id), label: channelOption(item) }))}
          onChange={(value) => filters.set({ channelId: value, rulesPage: null })}
          searchLabel={L("CFG1_SEARCH")}
          clearSearchLabel={L("CFG1_R1_CLEAR_SEARCH")}
          searchIcon={<ChatIcon name="search" />}
          clearIcon={<ChatIcon name="x" />}
          chevron={<ChatIcon name="chevron-down" />}
          check={<ChatIcon name="check" />}
        />
      </div>
      {rules.state === "loading" ? (
        <>
          <Counter>
            <span className={ui.bone} style={{ display: "inline-block", width: 60, height: 12 }} />
          </Counter>
          <RowsSkeleton rows={3} table={!mobile} />
        </>
      ) : rules.state !== "ready" ? (
        <ListFailed list={rules} />
      ) : rules.items.length === 0 && rules.total > 0 ? (
        <PastEnd onReset={() => filters.set({ rulesPage: null })} />
      ) : rules.items.length === 0 ? (
        <div className="tm-empty" role="status" data-part="vacio">
          <strong>{L("ADM_MSG_RULES_EMPTY")}</strong>
          <span>{L("ADM_MSG_RULES_EMPTY_LINE")}</span>
          <button type="button" className="tm-btn secondary" onClick={() => setCreating({ origin: "" })}>
            {L("ADM_MSG_NEW_RULE")}
          </button>
        </div>
      ) : (
        <>
          <Counter>{plural(L, locale, rules.total, "ADM_MSG_N_RULES_ONE", "ADM_MSG_N_RULES")}</Counter>
          <div className={own.fit}>
            <SimpleTable
              label={L("ADM_MSG_TAB_RULES")}
              rows={rules.items}
              rowKey={(rule) => rule.id}
              columns={[
                { key: "origin", title: L("ADM_MSG_COL_ORIGIN"), render: origin },
                { key: "inbox", title: L("ADM_MSG_COL_TARGET"), render: (rule) => rule.inbox ?? "—" },
                { key: "status", title: L("ADM_EMP_COL_STATUS"), render: ruleSwitch },
              ]}
              mobile={(rule) => ({
                title: originTitle(rule),
                line: [rule.headline ? rule.source : null, rule.inbox ?? "—"].filter(Boolean).join(" · "),
                end: ruleSwitch(rule),
              })}
            />
          </div>
          <ListFooter list={rules} />
        </>
      )}
      <section className={own.section} aria-labelledby="rules-origins">
        <h2 id="rules-origins" className={ui.subtitle}>
          {L("ADM_MSG_ORIGINS_TITLE")}
        </h2>
        <p className={ui.sectionLine}>{L("ADM_MSG_ORIGINS_LINE")}</p>
        {origins.state === "loading" ? (
          <RowsSkeleton rows={3} table={!mobile} />
        ) : origins.state !== "ready" ? (
          <ListFailed list={origins} />
        ) : origins.items.length === 0 && origins.total > 0 ? (
          <PastEnd onReset={() => filters.set({})} />
        ) : origins.items.length === 0 ? (
          <div className="tm-empty" role="status" data-part="vacio">
            <strong>{L("ADM_MSG_ORIGINS_EMPTY")}</strong>
            <span>{L("ADM_MSG_ORIGINS_EMPTY_LINE")}</span>
            <button type="button" className="tm-btn secondary" onClick={() => setCreating({ origin: "" })}>
              {L("ADM_MSG_NEW_RULE")}
            </button>
          </div>
        ) : (
          <>
            <div className={own.fit}>
              <SimpleTable
                label={L("ADM_MSG_ORIGINS_TITLE")}
                rows={origins.items}
                rowKey={(item) => item.source}
                columns={[
                  { key: "origin", title: L("ADM_MSG_COL_ORIGIN"), render: origin },
                  {
                    key: "seen",
                    title: L("ADM_MSG_COL_SEEN"),
                    numeric: true,
                    render: (item) => item.seen.toLocaleString(locale),
                  },
                  { key: "inbox", title: L("ADM_MSG_COL_GOES_TO"), render: (item) => item.inbox ?? "—" },
                  { key: "action", title: "", className: own.endCell, render: createFor },
                ]}
                mobile={(item) => ({
                  title: originTitle(item),
                  line: [item.seen.toLocaleString(locale), item.inbox].filter(Boolean).join(" · "),
                  end: createFor(item),
                })}
              />
            </div>
            <ListFooter list={origins} />
          </>
        )}
      </section>
      <RuleWindow
        orgId={orgId}
        channel={channel}
        inboxes={list.inboxes}
        draft={creating}
        onClose={() => setCreating(null)}
        onSaved={() => {
          rules.reload();
          origins.reload();
        }}
      />
    </>
  );
}

/**
 * Ventana `regla` · «Nueva regla» del canal elegido: origen (como aparece en el anuncio; si ya hay una regla para ese
 * origen, se reemplaza) y bandeja destino. Lo escrito se conserva ante un error del servidor.
 */
function RuleWindow({
  orgId,
  channel,
  inboxes,
  draft,
  onClose,
  onSaved,
}: {
  orgId: number;
  channel: Channel;
  inboxes: InboxOption[] | null;
  draft: { origin: string } | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { L } = useTexts();
  const toast = useToast();
  const [origin, setOrigin] = useState("");
  const [inbox, setInbox] = useState("");
  const [errors, setErrors] = useState<{ origin?: string | null; inbox?: string | null; form?: string | null }>({});
  const [busy, setBusy] = useState(false);
  const [shown, setShown] = useState(false);
  if (!!draft !== shown) {
    setShown(!!draft);
    if (draft) {
      setOrigin(draft.origin);
      setInbox(channel.defaultInboxId ? String(channel.defaultInboxId) : "");
      setErrors({});
    }
  }
  const options = (inboxes ?? []).map((item) => ({ value: String(item.id), label: item.name }));
  const create = async () => {
    const next = {
      origin: origin.trim() ? null : L("ADM_MSG_ERR_ORIGIN"),
      inbox: inbox ? null : L("ORGRULES_ERR_TARGET"),
    };
    if (next.origin || next.inbox) {
      setErrors(next);
      requestAnimationFrame(() =>
        document.getElementById(next.origin ? "rule-origin" : "rule-inbox")?.focus({ preventScroll: false }),
      );
      return;
    }
    setBusy(true);
    const reply = await postJson<{ saved?: boolean; messageKey?: string; field?: string }>(
      `/api/admin/organizations/${orgId}/channels/${channel.id}/rules`,
      { origin: origin.trim(), inboxId: Number(inbox) },
    );
    setBusy(false);
    if (reply.status === 200 && reply.body?.saved) {
      toast.show(L("ADM_MSG_RULE_CREATED"));
      onSaved();
      onClose();
      return;
    }
    const message = replyMessage(L, reply.body);
    setErrors(reply.body?.field === "origin" ? { origin: message } : { form: message });
  };
  return (
    <Window
      open={!!draft}
      title={L("ADM_MSG_NEW_RULE")}
      onClose={() => !busy && onClose()}
      footer={
        <>
          <button type="button" className="tm-btn secondary" onClick={onClose} disabled={busy}>
            {L("ACTION_CANCEL")}
          </button>
          <button
            type="button"
            className="tm-btn primary"
            disabled={busy || inboxes == null}
            aria-busy={busy || undefined}
            onClick={() => void create()}
          >
            {L("ADM_MSG_CREATE_RULE")}
          </button>
        </>
      }
    >
      <div className={ui.windowBlock}>
        <p className={own.windowLine}>{formatText(L("ADM_MSG_RULE_CHANNEL"), channelOption(channel))}</p>
        {errors.form ? <Notice tone="error">{errors.form}</Notice> : null}
        {inboxes == null ? <Notice tone="error">{L("ADM_UNAVAILABLE_LINE")}</Notice> : null}
        <TextField
          id="rule-origin"
          label={L("ADM_MSG_COL_ORIGIN")}
          value={origin}
          error={errors.origin}
          help={L("ADM_MSG_ORIGIN_HELP")}
          maxLength={200}
          placeholder={L("ADM_MSG_ORIGIN_EXAMPLE")}
          onChange={(value) => {
            setOrigin(value);
            if (value.trim()) setErrors((current) => ({ ...current, origin: null }));
          }}
        />
        <div className={`tm-field${errors.inbox ? " invalid" : ""}`}>
          <label htmlFor="rule-inbox">{L("ADM_MSG_COL_TARGET")}</label>
          <InboxChoice
            id="rule-inbox"
            label={L("ADM_MSG_COL_TARGET")}
            value={inbox}
            options={options}
            invalid={!!errors.inbox}
            onChange={(value) => (setInbox(value), setErrors((current) => ({ ...current, inbox: null })))}
          />
          {errors.inbox ? <span className="tm-field-error">{errors.inbox}</span> : null}
        </div>
      </div>
    </Window>
  );
}
